/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useEffect, useState } from 'react';
import { Text, TouchableOpacity, View } from 'react-native';
import { BlockHeading } from '../../shared/surface-cards/BlockHeading'
import { DenseListCard } from '../../shared/surface-cards/DenseListCard'
import { surfaceCardStyles as styles } from '../../shared/surface-cards/surfaceCards.styles';
import { colors } from '../../shared/theme/colors'
import { spacing } from '../../shared/theme/spacing';
import { fetchFiscalYtdTotal, fetchFiscalYtdTotalOrg } from '../infra/fiscalRepository'
import { getFiscalContext } from '../infra/fiscalConfigRepo'
import { monotributoLimitForCategory } from '../domain/fiscalPresentation';
import { handleLibroIvaExport } from '../application/libroIvaExport';
import { formatMoney } from '../../shared/format/public-api';

export type ReportsFiscalMonotributoSectionProps = {
  branchId: string | null;
  fiscalEnabledInPlan: boolean;
};

export function ReportsFiscalMonotributoSection({ branchId, fiscalEnabledInPlan }: ReportsFiscalMonotributoSectionProps) {
  const [ytd, setYtd] = useState<number | null>(null);
  const [limit, setLimit] = useState<number | null>(null);
  const [category, setCategory] = useState<string | null>(null);

  useEffect(() => {
    if (!branchId || !fiscalEnabledInPlan) return;
    let cancelled = false;
    // BUG-5 fix: resetear stale state al cambiar de sucursal.
    // Si la sucursal anterior era monotributo y la nueva es RI, category/limit
    // conservaban valores viejos y mostraban "Tope categoría A" para un RI.
    setCategory(null);
    setLimit(null);
    setYtd(null);
    (async () => {
      try {
        const ctx = await getFiscalContext(branchId).catch(() => null);
        const profile = ctx?.profile ?? null;
        const orgId = profile?.organizationId ?? ctx?.branchFiscal?.organizationId;
        const total = orgId
          ? await fetchFiscalYtdTotalOrg(orgId)
          : await fetchFiscalYtdTotal(branchId);
        if (cancelled) return;
        setYtd(total);
        if (profile?.taxRegime === 'monotributo') {
          setCategory(profile.monotributoCategory);
          setLimit(monotributoLimitForCategory(profile.monotributoCategory));
        }
      } catch {
        if (!cancelled) setYtd(0);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [branchId, fiscalEnabledInPlan]);

  if (!fiscalEnabledInPlan) return null;

  const handleExport = () => handleLibroIvaExport(branchId);

  return (
    <>
      <BlockHeading label="Monotributo y fiscal" />
      <DenseListCard>
        <Text style={styles.denseBlockTitle}>Tope categoría {category ?? '—'}</Text>
        <Text style={styles.surfaceCaption}>
          Facturado fiscal {new Date().getFullYear()} (org-wide):{' '}
          {ytd != null ? `${formatMoney(ytd)}` : '…'}
          {limit != null ? ` · Tope ref. ${formatMoney(limit)}` : ''}
        </Text>
        {limit != null && ytd != null && limit > 0 ? (
          <View style={{ marginTop: spacing.sm, height: 6, borderRadius: 3, backgroundColor: colors.border }}>
            <View
              style={{
                height: '100%',
                width: `${Math.min(100, Math.round((ytd / limit) * 100))}%`,
                backgroundColor: ytd / limit > 0.85 ? colors.danger : colors.accent,
                borderRadius: 3,
              }}
            />
          </View>
        ) : null}
        <TouchableOpacity
          style={[styles.secondaryButton, { marginTop: spacing.md }]}
          onPress={() => void handleExport()}
          accessibilityRole="button"
          accessibilityLabel="Exportar libro IVA"
        >
          <Text style={styles.secondaryButtonText}>Exportar libro IVA ventas (CSV)</Text>
        </TouchableOpacity>
      </DenseListCard>
    </>
  );
}
